import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    DollarSign, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownLeft, Plus, Trash2,
    Loader2, X, Search, Download, Edit, Save, Clock, AlertTriangle, Calendar, BarChart3, Tag,
    FileText, Wallet, PieChart, Activity, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';
import { ContextualTour } from '../../components/ContextualTour';
import { API } from '../../config/apiBase';


const FINANCEIRO_STEPS = [
    { target: '[data-testid="fin-resumo"]', title: 'Resumo Financeiro', content: 'Veja receitas, despesas e saldo do periodo selecionado. Use os filtros de data para ajustar.' },
    { target: '[data-testid="new-entry-btn"]', title: 'Novo Lancamento', content: 'Registre receitas ou despesas. Categorize e adicione descricoes para manter o controle.' },
    { target: '[data-testid="fin-lançamentos"]', title: 'Lancamentos', content: 'Todos os registros financeiros ficam aqui. Edite, exclua ou filtre por categoria.' },
    { target: '[data-testid="fin-contas"]', title: 'Contas e Categorias', content: 'Organize suas financas por contas bancarias e categorias personalizadas.' },
];
const DEFAULT_CATEGORIES = ['Folha de Pagamento', 'Infraestrutura', 'Serviços', 'Marketing', 'Operacional', 'Geral'];
const STATUS_OPTIONS = [
    { value: 'pago', label: 'Pago', color: '#22c55e' },
    { value: 'pendente', label: 'Pendente', color: '#f59e0b' },
    { value: 'cancelado', label: 'Cancelado', color: '#ef4444' },
];

export default function EmpresaFinanceiroPage() {
    const [tab, setTab] = useState('resumo');
    const [data, setData] = useState({ entries: [], income: 0, expense: 0, balance: 0 });
    const [summary, setSummary] = useState({ categories: {}, monthly: {}, pending: [], total_entries: 0 });
    const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [form, setForm] = useState({ description: '', type: 'expense', value: '', category: 'Geral', status: 'pago', notes: '', due_date: '' });
    const [saving, setSaving] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [newCatName, setNewCatName] = useState('');

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchLedger = useCallback(async () => {
        try {
            const res = await fetch(`${API}/ledger`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) setData(await res.json());
        } catch {} finally { setLoading(false); }
    }, [token]);

    const fetchSummary = useCallback(async () => {
        try {
            const res = await fetch(`${API}/ledger/summary`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) setSummary(await res.json());
        } catch {}
    }, [token]);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await fetch(`${API}/ledger/categories`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) {
                const cats = await res.json();
                setCategories([...new Set([...DEFAULT_CATEGORIES, ...cats.map(c => c.name)])]);
            }
        } catch {}
    }, [token]);

    useEffect(() => { fetchLedger(); fetchSummary(); fetchCategories(); }, [fetchLedger, fetchSummary, fetchCategories]);

    const openNewModal = () => {
        setEditingEntry(null);
        setForm({ description: '', type: 'expense', value: '', category: 'Geral', status: 'pago', notes: '', due_date: '' });
        setShowModal(true);
    };

    const openEditModal = (entry) => {
        setEditingEntry(entry);
        setForm({ description: entry.description, type: entry.type, value: String(entry.value), category: entry.category || 'Geral', status: entry.status || 'pago', notes: entry.notes || '', due_date: entry.due_date || '' });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.description.trim() || !form.value) { toast.error('Preencha descrição e valor'); return; }
        setSaving(true);
        try {
            const body = { ...form, value: parseFloat(form.value) };
            const url = editingEntry ? `${API}/ledger/${editingEntry.ledger_id}` : `${API}/ledger`;
            const method = editingEntry ? 'PUT' : 'POST';
            const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
            if (res.ok) {
                toast.success(editingEntry ? 'Lançamento atualizado' : 'Lançamento criado');
                setShowModal(false);
                fetchLedger(); fetchSummary();
            } else { const e = await res.json(); toast.error(e.detail || 'Erro'); }
        } catch { toast.error('Erro de conexão'); } finally { setSaving(false); }
    };

    const deleteEntry = async (id) => {
        if (!window.confirm('Excluir lançamento?')) return;
        const res = await fetch(`${API}/ledger/${id}`, { method: 'DELETE', headers });
        if (res.ok) { toast.success('Excluido'); fetchLedger(); fetchSummary(); }
    };

    const addCategory = async () => {
        if (!newCatName.trim()) return;
        try {
            const res = await fetch(`${API}/ledger/categories`, { method: 'POST', headers, body: JSON.stringify({ name: newCatName.trim() }) });
            if (res.ok) { toast.success('Categoria criada'); setNewCatName(''); fetchCategories(); }
        } catch { toast.error('Erro'); }
    };

    const deleteCategory = async (name) => {
        if (!window.confirm(`Excluir categoria "${name}"?`)) return;
        const res = await fetch(`${API}/ledger/categories/${encodeURIComponent(name)}`, { method: 'DELETE', headers });
        if (res.ok) { toast.success('Excluida'); fetchCategories(); }
    };

    const exportCSV = async () => {
        try {
            const res = await fetch(`${API}/ledger/export`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) {
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = 'financeiro_empresa.csv'; a.click();
                toast.success('CSV exportado');
            }
        } catch { toast.error('Erro ao exportar'); }
    };

    const exportPDF = async () => {
        try {
            const { jsPDF } = await import('jspdf');
            const { default: autoTable } = await import('jspdf-autotable');
            const doc = new jsPDF();
            doc.setFontSize(16);
            doc.text('Relatório Financeiro', 14, 20);
            doc.setFontSize(10);
            doc.text(`Receita: ${fmt(data.income)} | Despesas: ${fmt(data.expense)} | Saldo: ${fmt(data.balance)}`, 14, 30);
            doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, 14, 36);
            const rows = data.entries.map(e => [
                e.description, e.type === 'income' ? 'Receita' : 'Despesa', `R$ ${(e.value || 0).toFixed(2)}`,
                e.category || '', e.status || 'pago', e.created_at ? new Date(e.created_at).toLocaleDateString('pt-BR') : '',
            ]);
            autoTable(doc, { startY: 42, head: [['Descrição', 'Tipo', 'Valor', 'Categoria', 'Status', 'Data']], body: rows, styles: { fontSize: 8 } });
            doc.save('financeiro_empresa.pdf');
            toast.success('PDF exportado');
        } catch { toast.error('Erro ao gerar PDF'); }
    };

    const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

    const filteredEntries = useMemo(() => {
        return data.entries.filter(e => {
            if (filterType !== 'all' && e.type !== filterType) return false;
            if (filterStatus !== 'all' && (e.status || 'pago') !== filterStatus) return false;
            if (filterCategory !== 'all' && e.category !== filterCategory) return false;
            if (searchText && !e.description.toLowerCase().includes(searchText.toLowerCase())) return false;
            return true;
        });
    }, [data.entries, filterType, filterStatus, filterCategory, searchText]);

    const monthlyData = useMemo(() => {
        const m = summary.monthly || {};
        return Object.entries(m).slice(-6).map(([k, v]) => ({ month: k.slice(5), income: v.income || 0, expense: v.expense || 0 }));
    }, [summary.monthly]);

    const maxMonthly = useMemo(() => Math.max(1, ...monthlyData.map(m => Math.max(m.income, m.expense))), [monthlyData]);

    const totalEntries = data.entries.length;
    const paidCount = data.entries.filter(e => (e.status || 'pago') === 'pago').length;
    const pendingCount = data.entries.filter(e => e.status === 'pendente').length;

    const TABS = [
        { key: 'resumo', label: 'Resumo', icon: PieChart },
        { key: 'financeiro', label: 'Financeiro', icon: DollarSign },
        { key: 'contas', label: 'Contas', icon: Tag },
        { key: 'auditoria', label: 'Auditoria', icon: Activity },
    ];

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div className="space-y-4" data-testid="empresa-financeiro-page">
            <ContextualTour pageKey="financeiro" steps={FINANCEIRO_STEPS} />
            {/* Top actions */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex gap-2">
                    <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--muted)' }} onClick={exportCSV} data-testid="export-csv-btn">
                        <Download size={13} /> CSV
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--muted)' }} onClick={exportPDF} data-testid="export-pdf-btn">
                        <FileText size={13} /> PDF
                    </button>
                    <button className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} onClick={openNewModal} data-testid="new-entry-btn">
                        <Plus size={13} /> Novo Lançamento
                    </button>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                    { label: 'Receita Total', value: fmt(data.income), icon: TrendingUp, color: '#22c55e', sub: `${data.entries.filter(e => e.type === 'income').length} lançamento(s)` },
                    { label: 'Despesas Total', value: fmt(data.expense), icon: TrendingDown, color: '#ef4444', sub: `${data.entries.filter(e => e.type === 'expense').length} lançamento(s)` },
                    { label: 'Saldo Liquido', value: fmt(data.balance), icon: Wallet, color: data.balance >= 0 ? '#22c55e' : '#ef4444', sub: data.balance >= 0 ? 'Positivo' : 'Negativo' },
                    { label: 'Pendentes', value: pendingCount, icon: AlertTriangle, color: '#f59e0b', sub: pendingCount > 0 ? 'Requer atencao' : 'Tudo em dia' },
                ].map((c) => (
                    <div key={c.label} className="rounded-xl p-3.5 sm:p-4 transition-all" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center justify-between mb-2">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${c.color}12` }}>
                                <c.icon size={15} style={{ color: c.color }} />
                            </div>
                            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${c.color}10`, color: c.color }}>{c.sub}</span>
                        </div>
                        <p className="text-lg sm:text-xl font-bold" style={{ color: typeof c.value === 'string' ? c.color : 'var(--text)', fontFamily: "var(--font-display)" }}>{c.value}</p>
                        <p className="text-[10px] sm:text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{c.label}</p>
                    </div>
                ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-1.5 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }}>
                {TABS.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all"
                        style={{ background: tab === t.key ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface)', border: `1px solid ${tab === t.key ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`, color: tab === t.key ? 'var(--sv-accent)' : 'var(--muted)' }}
                        data-testid={`tab-${t.key}`}>
                        <t.icon size={12} /> {t.label}
                    </button>
                ))}
            </div>

            {/* ========== TAB: RESUMO ========== */}
            {tab === 'resumo' && (
                <div className="space-y-4" data-testid="fin-resumo">
                    {/* Visao geral do período */}
                    <div className="rounded-xl p-4 sm:p-5" style={{ background: 'linear-gradient(135deg, rgba(var(--sv-accent-rgb), 0.08) 0%, rgba(34,197,94,0.04) 100%)', border: '1px solid rgba(var(--sv-accent-rgb), 0.15)' }}>
                        <div className="flex items-center gap-2 mb-3">
                            <BarChart3 size={15} style={{ color: 'var(--sv-gold-primary)' }} />
                            <h3 className="text-sm font-semibold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Visao Geral</h3>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="text-center p-2.5 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                                <p className="text-lg font-bold" style={{ color: 'var(--text)' }}>{totalEntries}</p>
                                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Total lançamentos</p>
                            </div>
                            <div className="text-center p-2.5 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                                <p className="text-lg font-bold" style={{ color: '#22c55e' }}>{paidCount}</p>
                                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Pagos</p>
                            </div>
                            <div className="text-center p-2.5 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                                <p className="text-lg font-bold" style={{ color: '#f59e0b' }}>{pendingCount}</p>
                                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Pendentes</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Evolucao Mensal */}
                        <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Evolucao Mensal</h3>
                            {monthlyData.length === 0 ? (
                                <div className="text-center py-8">
                                    <BarChart3 size={28} className="mx-auto mb-2" style={{ color: 'var(--muted)', opacity: 0.4 }} />
                                    <p className="text-xs font-medium" style={{ color: 'var(--muted)' }}>Sem dados mensais</p>
                                    <p className="text-[10px] mt-1" style={{ color: 'var(--muted)', opacity: 0.6 }}>Crie lançamentos para ver a evolucao</p>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {monthlyData.map((m, i) => (
                                        <div key={i} className="flex items-center gap-3">
                                            <span className="text-xs w-8 font-medium" style={{ color: 'var(--muted)' }}>{m.month}</span>
                                            <div className="flex-1 flex gap-1">
                                                <div className="h-5 rounded-r transition-all" style={{ width: `${(m.income / maxMonthly) * 100}%`, background: '#22c55e', minWidth: m.income > 0 ? 4 : 0 }} />
                                                <div className="h-5 rounded-r transition-all" style={{ width: `${(m.expense / maxMonthly) * 100}%`, background: '#ef4444', minWidth: m.expense > 0 ? 4 : 0 }} />
                                            </div>
                                            <span className="text-[10px] w-24 text-right font-medium" style={{ color: (m.income - m.expense) >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(m.income - m.expense)}</span>
                                        </div>
                                    ))}
                                    <div className="flex gap-4 mt-2 text-[10px]" style={{ color: 'var(--muted)' }}>
                                        <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: '#22c55e' }} />Receita</span>
                                        <span><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: '#ef4444' }} />Despesa</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Por Categoria */}
                        <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Por Categoria</h3>
                            {Object.keys(summary.categories || {}).length === 0 ? (
                                <div className="text-center py-8">
                                    <PieChart size={28} className="mx-auto mb-2" style={{ color: 'var(--muted)', opacity: 0.4 }} />
                                    <p className="text-xs font-medium" style={{ color: 'var(--muted)' }}>Sem categorias</p>
                                    <p className="text-[10px] mt-1" style={{ color: 'var(--muted)', opacity: 0.6 }}>Classifique seus lançamentos</p>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {Object.entries(summary.categories).map(([cat, vals]) => {
                                        const bal = (vals.income || 0) - (vals.expense || 0);
                                        return (
                                            <div key={cat} className="flex items-center justify-between py-2 px-2 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                                                <div className="flex items-center gap-2">
                                                    <Tag size={11} style={{ color: 'var(--sv-gold-primary)' }} />
                                                    <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>{cat}</span>
                                                </div>
                                                <div className="flex gap-3 text-[11px]">
                                                    <span style={{ color: '#22c55e' }}>+{fmt(vals.income || 0)}</span>
                                                    <span style={{ color: '#ef4444' }}>-{fmt(vals.expense || 0)}</span>
                                                    <span className="font-bold" style={{ color: bal >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(bal)}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Insights */}
                    <div className="rounded-xl p-4" style={{ background: 'rgba(var(--sv-accent-rgb), 0.05)', border: '1px solid rgba(var(--sv-accent-rgb), 0.12)' }}>
                        <div className="flex items-center gap-2 mb-2">
                            <Lightbulb size={13} style={{ color: 'var(--sv-gold-primary)' }} />
                            <span className="text-xs font-semibold" style={{ color: 'var(--sv-gold-primary)' }}>Insights Rápidos</span>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            {totalEntries === 0 ? (
                                <p className="text-xs" style={{ color: 'var(--muted)' }}>Adicione lançamentos para receber insights automáticos sobre sua operação financeira.</p>
                            ) : (
                                <>
                                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                                        {data.balance >= 0
                                            ? `Saldo positivo de ${fmt(data.balance)}. Sua operação esta saudavel.`
                                            : `Atencao: saldo negativo de ${fmt(data.balance)}. Revise suas despesas.`}
                                    </p>
                                    {pendingCount > 0 && (
                                        <p className="text-xs" style={{ color: '#f59e0b' }}>
                                            {pendingCount} lançamento(s) pendente(s) aguardam pagamento ou confirmacao.
                                        </p>
                                    )}
                                    {Object.keys(summary.categories || {}).length > 0 && (() => {
                                        const topCat = Object.entries(summary.categories).sort((a, b) => (b[1].expense || 0) - (a[1].expense || 0))[0];
                                        return topCat ? <p className="text-xs" style={{ color: 'var(--muted)' }}>Maior despesa por categoria: <strong style={{ color: 'var(--text)' }}>{topCat[0]}</strong> ({fmt(topCat[1].expense || 0)})</p> : null;
                                    })()}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Pendentes destaque */}
                    {(summary.pending || []).length > 0 && (
                        <div className="rounded-xl p-4" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}>
                            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: '#f59e0b' }}>
                                <AlertTriangle size={13} /> Lançamentos Pendentes ({summary.pending.length})
                            </h3>
                            <div className="flex flex-col gap-1.5">
                                {summary.pending.slice(0, 5).map(e => (
                                    <div key={e.ledger_id} className="flex items-center justify-between py-1.5">
                                        <span className="text-xs" style={{ color: 'var(--text)' }}>{e.description}</span>
                                        <div className="flex items-center gap-2">
                                            {e.due_date && <span className="text-[10px]" style={{ color: 'var(--muted)' }}>{e.due_date}</span>}
                                            <span className="text-xs font-bold" style={{ color: e.type === 'income' ? '#22c55e' : '#ef4444' }}>{fmt(e.value)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ========== TAB: FINANCEIRO ========== */}
            {tab === 'financeiro' && (
                <div className="space-y-3" data-testid="fin-lançamentos">
                    {/* Filters */}
                    <div className="rounded-xl p-3 sm:p-4 space-y-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }} />
                            <input type="text" placeholder="Buscar lançamento..." value={searchText} onChange={e => setSearchText(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="fin-search" />
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <div className="flex gap-1">
                                {[['all', 'Todos'], ['income', 'Receitas'], ['expense', 'Despesas']].map(([k, l]) => (
                                    <button key={k} className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all"
                                        style={{ background: filterType === k ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface-2)', border: `1px solid ${filterType === k ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`, color: filterType === k ? 'var(--sv-accent)' : 'var(--muted)' }}
                                        onClick={() => setFilterType(k)}>{l}</button>
                                ))}
                            </div>
                            <div className="flex gap-1">
                                {[['all', 'Status'], ...STATUS_OPTIONS.map(s => [s.value, s.label])].map(([k, l]) => (
                                    <button key={k} className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all"
                                        style={{ background: filterStatus === k ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface-2)', border: `1px solid ${filterStatus === k ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`, color: filterStatus === k ? 'var(--sv-accent)' : 'var(--muted)' }}
                                        onClick={() => setFilterStatus(k)}>{l}</button>
                                ))}
                            </div>
                            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                                className="px-2.5 py-1.5 rounded-lg text-[11px]" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="fin-cat-filter">
                                <option value="all">Todas categorias</option>
                                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Count */}
                    <div className="flex items-center justify-between px-1">
                        <span className="text-xs" style={{ color: 'var(--muted)' }}>{filteredEntries.length} lançamento(s)</span>
                    </div>

                    {/* Entries */}
                    {filteredEntries.length === 0 ? (
                        <div className="text-center py-14 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <DollarSign size={32} className="mx-auto mb-2" style={{ color: 'var(--muted)', opacity: 0.4 }} />
                            <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Nenhum lançamento encontrado</p>
                            <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                                {data.entries.length === 0 ? 'Clique em "Novo Lançamento" para comecar' : 'Ajuste os filtros para encontrar lançamentos'}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {filteredEntries.map((e) => {
                                const st = STATUS_OPTIONS.find(s => s.value === (e.status || 'pago'));
                                return (
                                    <div key={e.ledger_id} className="rounded-xl p-3.5 transition-all" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`entry-${e.ledger_id}`}>
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: e.type === 'income' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)' }}>
                                                {e.type === 'income' ? <ArrowUpRight size={14} style={{ color: '#22c55e' }} /> : <ArrowDownLeft size={14} style={{ color: '#ef4444' }} />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{e.description}</p>
                                                    {st && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: `${st.color}12`, color: st.color }}>{st.label}</span>}
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }}>{e.category}</span>
                                                </div>
                                                {e.notes && <p className="text-[11px] mt-0.5" style={{ color: 'var(--muted)' }}>{e.notes}</p>}
                                                <div className="flex items-center gap-3 mt-1 text-[10px]" style={{ color: 'var(--muted)' }}>
                                                    <span>{e.created_at ? new Date(e.created_at).toLocaleDateString('pt-BR') : ''}</span>
                                                    {e.due_date && <span style={{ color: '#f59e0b' }}>Venc: {e.due_date}</span>}
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className="text-sm font-bold" style={{ color: e.type === 'income' ? '#22c55e' : '#ef4444' }}>
                                                    {e.type === 'income' ? '+' : '-'}{fmt(e.value)}
                                                </p>
                                                <div className="flex gap-0.5 mt-1 justify-end">
                                                    <button className="p-1 rounded hover:opacity-80" style={{ color: 'var(--muted)' }} onClick={() => openEditModal(e)} data-testid={`edit-${e.ledger_id}`}><Edit size={11} /></button>
                                                    <button className="p-1 rounded hover:opacity-80" style={{ color: '#ef4444' }} onClick={() => deleteEntry(e.ledger_id)}><Trash2 size={11} /></button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ========== TAB: CONTAS / CATEGORIAS ========== */}
            {tab === 'contas' && (
                <div className="space-y-4" data-testid="fin-contas">
                    {/* Add category */}
                    <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Categorias Financeiras</h3>
                        <div className="flex gap-2 mb-4">
                            <input type="text" placeholder="Nova categoria..." value={newCatName} onChange={e => setNewCatName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && addCategory()}
                                className="flex-1 px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="new-cat-input" />
                            <button className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} onClick={addCategory} data-testid="add-cat-btn"><Plus size={13} /> Adicionar</button>
                        </div>

                        {/* Categories list */}
                        <div className="flex flex-col gap-2">
                            {categories.map(cat => {
                                const catData = summary.categories?.[cat] || {};
                                const inc = catData.income || 0;
                                const exp = catData.expense || 0;
                                const bal = inc - exp;
                                const hasData = inc > 0 || exp > 0;
                                return (
                                    <div key={cat} className="flex items-center justify-between p-3 rounded-xl transition-all" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }} data-testid={`cat-${cat}`}>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)' }}>
                                                <Tag size={13} style={{ color: 'var(--sv-gold-primary)' }} />
                                            </div>
                                            <div>
                                                <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{cat}</span>
                                                {!hasData && <p className="text-[10px]" style={{ color: 'var(--muted)', opacity: 0.5 }}>Sem movimentacao</p>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {hasData && (
                                                <div className="text-right">
                                                    <div className="text-[11px]" style={{ color: '#22c55e' }}>+{fmt(inc)}</div>
                                                    <div className="text-[11px]" style={{ color: '#ef4444' }}>-{fmt(exp)}</div>
                                                </div>
                                            )}
                                            <span className="text-sm font-bold min-w-[80px] text-right" style={{ color: hasData ? (bal >= 0 ? '#22c55e' : '#ef4444') : 'var(--muted)' }}>
                                                {hasData ? fmt(bal) : 'R$ 0,00'}
                                            </span>
                                            {!DEFAULT_CATEGORIES.includes(cat) && (
                                                <button className="p-1 rounded hover:opacity-80" style={{ color: '#ef4444' }} onClick={() => deleteCategory(cat)}><Trash2 size={12} /></button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Summary */}
                    <div className="rounded-xl p-4" style={{ background: 'rgba(var(--sv-accent-rgb), 0.04)', border: '1px solid rgba(var(--sv-accent-rgb), 0.1)' }}>
                        <p className="text-xs" style={{ color: 'var(--muted)' }}>
                            <strong style={{ color: 'var(--sv-gold-primary)' }}>{categories.length}</strong> categoria(s) cadastrada(s).
                            As categorias padrão não podem ser removidas. Categorias personalizadas podem ser excluídas.
                        </p>
                    </div>
                </div>
            )}

            {/* ========== TAB: AUDITORIA ========== */}
            {tab === 'auditoria' && (
                <div className="space-y-4" data-testid="fin-auditoria">
                    <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Histórico de Atividades</h3>
                            <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }}>
                                {data.entries.length} registro(s)
                            </span>
                        </div>
                        {data.entries.length === 0 ? (
                            <div className="text-center py-14">
                                <Activity size={32} className="mx-auto mb-2" style={{ color: 'var(--muted)', opacity: 0.4 }} />
                                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Nenhuma atividade</p>
                                <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>O histórico aparecera conforme você criar lançamentos</p>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-0.5">
                                {data.entries.map((e, i) => {
                                    const st = STATUS_OPTIONS.find(x => x.value === (e.status || 'pago'));
                                    return (
                                        <div key={e.ledger_id} className="flex items-start gap-3 py-3" style={{ borderBottom: i < data.entries.length - 1 ? '1px solid var(--border)' : 'none' }} data-testid={`audit-${e.ledger_id}`}>
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: e.type === 'income' ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)' }}>
                                                {e.type === 'income' ? <ArrowUpRight size={13} style={{ color: '#22c55e' }} /> : <ArrowDownLeft size={13} style={{ color: '#ef4444' }} />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{e.description}</span>
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }}>{e.category}</span>
                                                    {st && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: `${st.color}15`, color: st.color }}>{st.label}</span>}
                                                </div>
                                                {e.notes && <p className="text-[11px] mt-0.5" style={{ color: 'var(--muted)' }}>{e.notes}</p>}
                                                <div className="flex gap-3 mt-1 text-[10px]" style={{ color: 'var(--muted)' }}>
                                                    <span><Calendar size={9} className="inline mr-0.5" />Criado: {e.created_at ? new Date(e.created_at).toLocaleString('pt-BR') : '-'}</span>
                                                    {e.updated_at && <span><Clock size={9} className="inline mr-0.5" />Editado: {new Date(e.updated_at).toLocaleString('pt-BR')}</span>}
                                                    {e.due_date && <span><AlertTriangle size={9} className="inline mr-0.5" />Venc: {e.due_date}</span>}
                                                </div>
                                            </div>
                                            <span className="text-sm font-bold shrink-0" style={{ color: e.type === 'income' ? '#22c55e' : '#ef4444' }}>
                                                {e.type === 'income' ? '+' : '-'}{fmt(e.value)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ========== MODAL ========== */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
                    <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl p-5 sm:p-6" style={{ background: 'var(--surface)', border: '1px solid rgba(var(--sv-accent-rgb), 0.25)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }} data-testid="fin-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>
                                {editingEntry ? 'Editar Lançamento' : 'Novo Lançamento'}
                            </h3>
                            <button onClick={() => setShowModal(false)} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }}><X size={14} style={{ color: 'var(--muted)' }} /></button>
                        </div>
                        <div className="flex flex-col gap-3">
                            <div>
                                <label className="text-[11px] font-medium mb-1.5 block" style={{ color: 'var(--muted)' }}>Tipo</label>
                                <div className="flex gap-2">
                                    {[['income', 'Receita', '#22c55e'], ['expense', 'Despesa', '#ef4444']].map(([k, l, c]) => (
                                        <button key={k} className="flex-1 px-3 py-2.5 rounded-lg text-xs font-medium transition-all"
                                            style={{ background: form.type === k ? `${c}12` : 'var(--surface-2)', border: `1px solid ${form.type === k ? `${c}30` : 'var(--border)'}`, color: form.type === k ? c : 'var(--muted)' }}
                                            onClick={() => setForm({ ...form, type: k })}>{l}</button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] font-medium mb-1.5 block" style={{ color: 'var(--muted)' }}>Descrição</label>
                                <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Ex: Pagamento servico"
                                    className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="fin-desc-input" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[11px] font-medium mb-1.5 block" style={{ color: 'var(--muted)' }}>Valor (R$)</label>
                                    <input type="number" step="0.01" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="0.00"
                                        className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="fin-value-input" />
                                </div>
                                <div>
                                    <label className="text-[11px] font-medium mb-1.5 block" style={{ color: 'var(--muted)' }}>Vencimento</label>
                                    <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })}
                                        className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="fin-date-input" />
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] font-medium mb-1.5 block" style={{ color: 'var(--muted)' }}>Categoria</label>
                                <div className="flex gap-1.5 flex-wrap">
                                    {categories.map(c => (
                                        <button key={c} className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all"
                                            style={{ background: form.category === c ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface-2)', border: `1px solid ${form.category === c ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`, color: form.category === c ? 'var(--sv-accent)' : 'var(--muted)' }}
                                            onClick={() => setForm({ ...form, category: c })}>{c}</button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] font-medium mb-1.5 block" style={{ color: 'var(--muted)' }}>Status</label>
                                <div className="flex gap-2">
                                    {STATUS_OPTIONS.map(s => (
                                        <button key={s.value} className="px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all"
                                            style={{ background: form.status === s.value ? `${s.color}12` : 'var(--surface-2)', border: `1px solid ${form.status === s.value ? `${s.color}30` : 'var(--border)'}`, color: form.status === s.value ? s.color : 'var(--muted)' }}
                                            onClick={() => setForm({ ...form, status: s.value })}>{s.label}</button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] font-medium mb-1.5 block" style={{ color: 'var(--muted)' }}>Observacoes</label>
                                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Notas..."
                                    className="w-full px-3 py-2.5 rounded-lg text-sm resize-none" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="fin-notes-input" />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button className="px-4 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--border)' }} onClick={() => setShowModal(false)}>Cancelar</button>
                            <button className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} onClick={handleSave} disabled={saving} data-testid="fin-save-btn">
                                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} {editingEntry ? 'Salvar' : 'Criar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
